import { computed, reactive, watch } from 'vue'

// Estado global del Pomodoro
const pomodoroState = reactive({
  time: 25 * 60, // 25 minutos por defecto
  isRunning: false,
  mode: 'work', // 'work', 'shortBreak', 'longBreak'
  session: 1,
  totalSessions: 0,
  completedPomodoros: 0,
  completedBreaks: 0,
  isPaused: false,
  startTime: null,
  endTime: null
})

// Configuración persistente
const settings = reactive({
  workDuration: 25, // minutos
  shortBreakDuration: 5, // minutos
  longBreakDuration: 15, // minutos
  longBreakInterval: 4, // cada 4 pomodoros
  autoStartBreaks: false,
  autoStartWork: false,
  notifications: true,
  sounds: true,
  soundVolume: 0.5,
  tickingSound: false,
  dailyGoal: 8 // pomodoros por día
})

// Estadísticas persistentes
const stats = reactive({
  today: {
    date: new Date().toDateString(),
    completed: 0,
    focused: 0, // minutos de trabajo efectivo
    breaks: 0
  },
  weekly: [],
  monthly: [],
  allTime: {
    totalPomodoros: 0,
    totalFocusTime: 0, // en minutos
    totalBreakTime: 0,
    averageSessionLength: 0,
    streak: 0,
    longestStreak: 0
  }
})

// Variables globales para el temporizador
let globalInterval = null
let tickingAudio = null
let completionAudio = null
let isTimerInitialized = false

// Cargar datos del localStorage al inicializar
const loadFromStorage = () => {
  try {
    const savedSettings = localStorage.getItem('pomodoro-settings')
    if (savedSettings) {
      Object.assign(settings, JSON.parse(savedSettings))
    }

    const savedStats = localStorage.getItem('pomodoro-stats')
    if (savedStats) {
      const parsed = JSON.parse(savedStats)
      Object.assign(stats, parsed)
      
      // Verificar si cambió el día
      if (stats.today.date !== new Date().toDateString()) {
        // Guardar día anterior en weekly si tiene datos
        if (stats.today.completed > 0) {
          stats.weekly.unshift({ ...stats.today })
          if (stats.weekly.length > 7) stats.weekly.pop()
        }
        
        // Resetear estadísticas del día
        stats.today = {
          date: new Date().toDateString(),
          completed: 0,
          focused: 0,
          breaks: 0
        }
      }
    }

    const savedState = localStorage.getItem('pomodoro-state')
    if (savedState) {
      const parsed = JSON.parse(savedState)
      
      // Solo restaurar si la sesión no ha expirado (máximo 2 horas)
      const now = Date.now()
      const timeSinceSave = now - (parsed.lastSave || 0)
      
      if (timeSinceSave < 2 * 60 * 60 * 1000) { // 2 horas
        Object.assign(pomodoroState, {
          mode: parsed.mode,
          session: parsed.session,
          totalSessions: parsed.totalSessions,
          completedPomodoros: parsed.completedPomodoros,
          completedBreaks: parsed.completedBreaks
        })
        
        // Restaurar tiempo si estaba corriendo
        if (parsed.isRunning && parsed.startTime) {
          const elapsed = Math.floor((now - parsed.startTime) / 1000)
          const remainingTime = parsed.time - elapsed
          
          if (remainingTime > 0) {
            pomodoroState.time = remainingTime
            pomodoroState.isRunning = true
            pomodoroState.startTime = parsed.startTime
            startTimer()
          } else {
            // El tiempo se agotó mientras estaba cerrado
            pomodoroState.time = 0
            completeSession()
          }
        } else {
          pomodoroState.time = getTimeForMode(parsed.mode)
        }
      } else {
        // Sesión expirada, resetear
        resetTimer()
      }
    }
  } catch (error) {
    console.error('Error loading pomodoro data:', error)
  }
}

// Guardar en localStorage
const saveToStorage = () => {
  try {
    localStorage.setItem('pomodoro-settings', JSON.stringify(settings))
    localStorage.setItem('pomodoro-stats', JSON.stringify(stats))
    localStorage.setItem('pomodoro-state', JSON.stringify({
      ...pomodoroState,
      lastSave: Date.now()
    }))
  } catch (error) {
    console.error('Error saving pomodoro data:', error)
  }
}

// Watchers para auto-guardar
watch(() => settings, saveToStorage, { deep: true })
watch(() => stats, saveToStorage, { deep: true })
watch(() => pomodoroState, saveToStorage, { deep: true })

// Obtener tiempo según el modo
const getTimeForMode = (mode) => {
  switch (mode) {
    case 'work':
      return settings.workDuration * 60
    case 'shortBreak':
      return settings.shortBreakDuration * 60
    case 'longBreak':
      return settings.longBreakDuration * 60
    default:
      return settings.workDuration * 60
  }
}

// Reproducir sonidos
const playSound = (type) => {
  if (!settings.sounds) return

  try {
    if (type === 'complete') {
      if (!completionAudio) {
        completionAudio = new Audio()
        completionAudio.volume = settings.soundVolume
        // Usar un sonido simple generado por código
        completionAudio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAaBjyA2O/CeSEEKnbL8N6GOwgRa7Xk36JLCw5Mp+LrumEXBUOA0vHHfijvA='
      }
      completionAudio.currentTime = 0
      completionAudio.play().catch(() => {})
    } else if (type === 'tick' && settings.tickingSound) {
      // Sonido de tick simple
      if (!tickingAudio) {
        tickingAudio = new Audio()
        tickingAudio.volume = settings.soundVolume * 0.1
        tickingAudio.src = 'data:audio/wav;base64,UklGRkIGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YSAGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/AabjiI2e6E='
      }
      tickingAudio.currentTime = 0
      tickingAudio.play().catch(() => {})
    }
  } catch (error) {
    console.warn('Could not play sound:', error)
  }
}

// Enviar notificación
const sendNotification = (title, body, icon = '/favicon.ico') => {
  if (!settings.notifications) return

  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const notification = new Notification(title, { body, icon })
      setTimeout(() => notification.close(), 5000)
    } catch (error) {
      console.warn('Could not send notification:', error)
    }
  }
}

// Completar sesión
const completeSession = () => {
  const wasWorking = pomodoroState.mode === 'work'
  
  if (wasWorking) {
    pomodoroState.completedPomodoros++
    stats.today.completed++
    stats.today.focused += settings.workDuration
    stats.allTime.totalPomodoros++
    stats.allTime.totalFocusTime += settings.workDuration
    
    // Actualizar racha
    stats.allTime.streak++
    if (stats.allTime.streak > stats.allTime.longestStreak) {
      stats.allTime.longestStreak = stats.allTime.streak
    }
    
    // Determinar próximo modo
    if (pomodoroState.session % settings.longBreakInterval === 0) {
      pomodoroState.mode = 'longBreak'
    } else {
      pomodoroState.mode = 'shortBreak'
    }
    
    pomodoroState.session++
    
    playSound('complete')
    sendNotification(
      '🍅 ¡Pomodoro Completado!',
      `Sesión de trabajo completada. Tiempo para ${pomodoroState.mode === 'longBreak' ? 'un descanso largo' : 'un descanso corto'}.`
    )
  } else {
    // Era un descanso
    pomodoroState.completedBreaks++
    stats.today.breaks++
    stats.allTime.totalBreakTime += pomodoroState.mode === 'longBreak' 
      ? settings.longBreakDuration 
      : settings.shortBreakDuration
    
    pomodoroState.mode = 'work'
    
    playSound('complete')
    sendNotification(
      '☕ ¡Descanso Completado!',
      'Descanso terminado. ¡Hora de volver al trabajo!'
    )
  }
  
  pomodoroState.time = getTimeForMode(pomodoroState.mode)
  pomodoroState.isRunning = false
  pomodoroState.isPaused = false
  
  // Auto-iniciar si está configurado
  const shouldAutoStart = (pomodoroState.mode === 'work' && settings.autoStartWork) ||
                         (pomodoroState.mode !== 'work' && settings.autoStartBreaks)
  
  if (shouldAutoStart) {
    setTimeout(() => startTimer(), 1000)
  }
  
  // Actualizar título de la página
  updatePageTitle()
}

// Iniciar temporizador
const startTimer = () => {
  if (globalInterval) clearInterval(globalInterval)
  
  pomodoroState.isRunning = true
  pomodoroState.isPaused = false
  pomodoroState.startTime = Date.now() - ((getTimeForMode(pomodoroState.mode) - pomodoroState.time) * 1000)
  
  globalInterval = setInterval(() => {
    if (pomodoroState.time > 0) {
      pomodoroState.time--
      
      if (settings.tickingSound && pomodoroState.time % 60 === 0) {
        playSound('tick')
      }
      
      updatePageTitle()
    } else {
      clearInterval(globalInterval)
      globalInterval = null
      completeSession()
    }
  }, 1000)
  
  updatePageTitle()
}

// Pausar temporizador
const pauseTimer = () => {
  if (globalInterval) {
    clearInterval(globalInterval)
    globalInterval = null
  }
  
  pomodoroState.isRunning = false
  pomodoroState.isPaused = true
  updatePageTitle()
}

// Resetear temporizador
const resetTimer = () => {
  if (globalInterval) {
    clearInterval(globalInterval)
    globalInterval = null
  }
  
  pomodoroState.isRunning = false
  pomodoroState.isPaused = false
  pomodoroState.time = getTimeForMode(pomodoroState.mode)
  pomodoroState.startTime = null
  
  updatePageTitle()
}

// Cambiar modo manualmente
const switchMode = (mode) => {
  pauseTimer()
  pomodoroState.mode = mode
  pomodoroState.time = getTimeForMode(mode)
  pomodoroState.isPaused = false
  updatePageTitle()
}

// Actualizar título de la página
const updatePageTitle = () => {
  if (typeof document === 'undefined') return
  
  const originalTitle = document.title.split(' - ')[1] || document.title
  
  if (pomodoroState.isRunning || pomodoroState.isPaused) {
    const minutes = Math.floor(pomodoroState.time / 60)
    const seconds = pomodoroState.time % 60
    const timeStr = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    const status = pomodoroState.isRunning ? '🍅' : '⏸️'
    const mode = pomodoroState.mode === 'work' ? 'Trabajo' : 'Descanso'
    
    document.title = `${status} ${timeStr} ${mode} - ${originalTitle}`
  } else {
    document.title = originalTitle
  }
}

// Solicitar permisos de notificación
const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    } catch (error) {
      console.warn('Could not request notification permission:', error)
      return false
    }
  }
  return Notification.permission === 'granted'
}

// Computed properties
const formattedTime = computed(() => {
  const minutes = Math.floor(pomodoroState.time / 60)
  const seconds = pomodoroState.time % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
})

const progress = computed(() => {
  const totalTime = getTimeForMode(pomodoroState.mode)
  return ((totalTime - pomodoroState.time) / totalTime) * 100
})

const currentModeLabel = computed(() => {
  switch (pomodoroState.mode) {
    case 'work':
      return 'Trabajo'
    case 'shortBreak':
      return 'Descanso Corto'
    case 'longBreak':
      return 'Descanso Largo'
    default:
      return 'Trabajo'
  }
})

const todayProgress = computed(() => {
  return Math.min((stats.today.completed / settings.dailyGoal) * 100, 100)
})

const canStart = computed(() => {
  return !pomodoroState.isRunning && pomodoroState.time > 0
})

const canPause = computed(() => {
  return pomodoroState.isRunning
})

const canReset = computed(() => {
  return pomodoroState.isPaused || pomodoroState.time !== getTimeForMode(pomodoroState.mode)
})

// Inicializar el temporizador globalmente una sola vez
const initializeGlobalTimer = () => {
  if (isTimerInitialized) return
  
  if (typeof window !== 'undefined') {
    loadFromStorage()
    requestNotificationPermission()
    isTimerInitialized = true
    
    // Limpiar al cerrar la ventana
    window.addEventListener('beforeunload', () => {
      if (globalInterval) {
        clearInterval(globalInterval)
      }
    })
  }
}

// Función principal del composable
export function usePomodoro() {
  // Inicializar solo una vez globalmente
  initializeGlobalTimer()
  
  // No limpiar el interval al desmontar componentes individuales
  // El temporizador debe seguir corriendo globalmente
  
  return {
    // Estado reactivo
    state: pomodoroState,
    settings,
    stats,
    
    // Computed
    formattedTime,
    progress,
    currentModeLabel,
    todayProgress,
    canStart,
    canPause,
    canReset,
    
    // Métodos
    startTimer,
    pauseTimer,
    resetTimer,
    switchMode,
    playSound,
    requestNotificationPermission,
    
    // Utilidades
    getTimeForMode,
    updatePageTitle
  }
}

// Exportar para uso global
export { pomodoroState, settings, stats }